import { useApolloClient } from '@apollo/client';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LOGIN_MUTATION, REFRESH_TOKEN_MUTATION } from './auth.mutations';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  picture?: string;
  email_verified?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const TOKEN_KEY = 'treston_access_token';
const REFRESH_TOKEN_KEY = 'treston_refresh_token';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const apolloClient = useApolloClient();

  const isAuthenticated = !!user;

  // Load user from token on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (token) {
      // Decode JWT to get user info (simple decode, not verification)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Date.now() / 1000;

        if (payload.exp > now) {
          // Token is still valid
          setUser({
            id: payload.userId,
            email: payload.email,
            name: payload.name || payload.email.split('@')[0],
            role: payload.role,
          });
        } else if (refreshToken) {
          // Token expired, try to refresh
          handleRefreshToken();
        } else {
          // No refresh token, clear storage
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
      } catch (error) {
        console.error('Invalid token:', error);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefreshToken = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      // Clear any stale access token if no refresh token exists
      localStorage.removeItem(TOKEN_KEY);
      return;
    }

    try {
      const { data } = await apolloClient.mutate({
        mutation: REFRESH_TOKEN_MUTATION,
        variables: { refreshToken },
      });

      const { user: userData, accessToken, refreshToken: newRefreshToken } = data.refreshToken;

      localStorage.setItem(TOKEN_KEY, accessToken);
      if (newRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      }

      setUser(userData);
    } catch (error) {
      console.error('Refresh token failed:', error);
      // Clear all tokens if refresh fails
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: LOGIN_MUTATION,
        variables: { email, password },
      });

      const { user: userData, accessToken, refreshToken } = data.login;

      localStorage.setItem(TOKEN_KEY, accessToken);
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }

      setUser(userData);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };


  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
    apolloClient.clearStore();

    // Redirect to root page after logout
    window.location.href = '/';
  };

  const refreshToken = async () => {
    await handleRefreshToken();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { TOKEN_KEY, REFRESH_TOKEN_KEY };